/**
 * 自由创作「素材」行(0924 车 B)—— 想拍什么之下、风格之上。
 *
 * 三选一 segmented:不用素材 / 上传首帧图(可再传尾帧)/ 参考图(≤4)。
 * 状态与互斥规则全在 `model/freeMaterial.ts`(纯函数、有单测);本组件只管上传与展示。
 * 人物照:客户端不检测人脸,只放文案把人引去人物库 / 真人出镜(老板拍板)。
 */
import { ImagePlus, Loader2, X } from "lucide-react";
import { useRef, useState, type Dispatch, type SetStateAction } from "react";

import { IMAGE_ACCEPT } from "@/shared/lib/uploadImageFormats";

import { videoService } from "../../api/videoService";
import {
  FREE_MATERIAL_HINT,
  FREE_MATERIAL_PERSON_NOTE,
  FREE_REF_MAX,
  addFreeRefImages,
  freeMaterialModeOf,
  removeFreeRefImage,
  switchFreeMaterialMode,
  withFirstFrame,
  withLastFrame,
  withoutFirstFrame,
} from "../../model/freeMaterial";
import type { FormState, FreeMaterialMode, FreeRefImage } from "../../model/videoCreateTypes";

import { OptionChip, OptionChipGroup } from "./OptionChip";
import { errorText } from "@/shared/lib/apiErrors";

const MODES: { value: FreeMaterialMode; label: string }[] = [
  { value: "none", label: "不用素材" },
  { value: "frame", label: "上传首帧图" },
  { value: "refs", label: `参考图(≤${FREE_REF_MAX})` },
];

interface Props {
  form: FormState;
  setForm: Dispatch<SetStateAction<FormState>>;
  disabled?: boolean;
  /** 上传口(默认 videoService.uploadFrame;测试可注入)。 */
  upload?: (file: File) => Promise<{ url: string; key: string }>;
}

type Slot = "first" | "last" | "refs";

function Thumb({
  img,
  label,
  onRemove,
  testId,
}: {
  img: FreeRefImage;
  label: string;
  onRemove: () => void;
  testId: string;
}) {
  return (
    <div className="relative h-20 w-20 shrink-0" data-testid={testId}>
      <img
        src={img.url}
        alt={label}
        className="h-full w-full rounded-md border border-border/60 object-cover"
      />
      <span className="absolute bottom-0.5 left-0.5 rounded bg-background/80 px-1 text-[10px] text-foreground">
        {label}
      </span>
      <button
        type="button"
        aria-label={`删除${label}`}
        onClick={onRemove}
        className="absolute -right-1.5 -top-1.5 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border bg-background text-muted-foreground hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

export function FreeMaterialRow({
  form,
  setForm,
  disabled,
  upload = videoService.uploadFrame,
}: Props) {
  const mode = freeMaterialModeOf(form);
  const fileRef = useRef<HTMLInputElement>(null);
  // 用 ref 不用 state:点按钮与 file input 的 change 之间不保证已重渲染。
  const slotRef = useRef<Slot>("first");
  const [busy, setBusy] = useState<Slot | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refs = form.freeRefImages ?? [];
  const pick = (s: Slot) => {
    slotRef.current = s;
    if (fileRef.current) {
      fileRef.current.multiple = s === "refs";
      fileRef.current.click();
    }
  };

  const onFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const picked = Array.from(files);
    if (picked.some((f) => !/^image\/(png|jpeg|webp)$/.test(f.type))) {
      setError("图片仅支持 png/jpeg/webp");
      return;
    }
    const slot = slotRef.current;
    setError(null);
    setBusy(slot);
    try {
      if (slot === "refs") {
        const room = Math.max(0, FREE_REF_MAX - refs.length);
        const uploaded = await Promise.all(picked.slice(0, room).map((f) => upload(f)));
        setForm((f) => addFreeRefImages(f, uploaded));
      } else {
        const img = await upload(picked[0]);
        setForm((f) => (slot === "first" ? withFirstFrame(f, img) : withLastFrame(f, img)));
      }
    } catch (e) {
      setError(errorText(e, { fallbackTitle: "上传失败,请重试" }));
    } finally {
      setBusy(null);
    }
  };

  const addBtn = (s: Slot, text: string, testId: string) => (
    <button
      type="button"
      disabled={disabled || busy !== null}
      onClick={() => pick(s)}
      data-testid={testId}
      className="flex h-20 w-20 shrink-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border/70 text-2xs text-muted-foreground transition-colors hover:border-primary/60 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50"
    >
      {busy === s ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <ImagePlus className="h-4 w-4" />
      )}
      {text}
    </button>
  );

  const firstImg =
    form.firstFrameKey && form.firstFrameUrl
      ? { key: form.firstFrameKey, url: form.firstFrameUrl }
      : null;
  const lastImg =
    form.lastFrameKey && form.lastFrameUrl
      ? { key: form.lastFrameKey, url: form.lastFrameUrl }
      : null;

  return (
    <div className="space-y-2" data-testid="video-free-material-row">
      <span className="block text-sm text-muted-foreground">素材</span>
      <OptionChipGroup label="素材">
        {MODES.map((m) => (
          <OptionChip
            key={m.value}
            radio
            active={mode === m.value}
            tabIndex={mode === m.value ? 0 : -1}
            disabled={disabled}
            data-testid={`video-free-material-${m.value}`}
            onClick={() => setForm((f) => switchFreeMaterialMode(f, m.value))}
          >
            {m.label}
          </OptionChip>
        ))}
      </OptionChipGroup>
      <p className="text-2xs text-muted-foreground">{FREE_MATERIAL_HINT[mode]}</p>

      {mode === "frame" && (
        <div className="flex flex-wrap items-start gap-3">
          {firstImg ? (
            <Thumb
              img={firstImg}
              label="首帧"
              testId="video-free-first-thumb"
              onRemove={() => setForm((f) => withoutFirstFrame(f))}
            />
          ) : (
            addBtn("first", "传首帧", "video-free-upload-first")
          )}
          {firstImg &&
            (lastImg ? (
              <Thumb
                img={lastImg}
                label="尾帧"
                testId="video-free-last-thumb"
                onRemove={() => setForm((f) => withLastFrame(f, null))}
              />
            ) : (
              addBtn("last", "尾帧(可选)", "video-free-upload-last")
            ))}
        </div>
      )}

      {mode === "refs" && (
        <div className="flex flex-wrap items-start gap-3">
          {refs.map((img, i) => (
            <Thumb
              key={img.key}
              img={img}
              label={`参考${i + 1}`}
              testId="video-free-ref-thumb"
              onRemove={() => setForm((f) => removeFreeRefImage(f, img.key))}
            />
          ))}
          {refs.length < FREE_REF_MAX && addBtn("refs", "加参考图", "video-free-upload-ref")}
        </div>
      )}

      {mode !== "none" && (
        <p className="text-2xs text-muted-foreground" data-testid="video-free-person-note">
          {FREE_MATERIAL_PERSON_NOTE}
        </p>
      )}
      {error && <p className="text-2xs text-destructive">{error}</p>}
      <input
        ref={fileRef}
        type="file"
        accept={IMAGE_ACCEPT}
        className="hidden"
        data-testid="video-free-material-file"
        onChange={(e) => {
          void onFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
}
